import { ApiError } from '../api/client';

/** Default lead time before access expiry when the guest is warned. */
export const GUEST_EXPIRY_WARNING_MS = 5 * 60 * 1000;

/** Delay before retrying a gate refresh that failed due to rate limiting. */
export const GUEST_EXPIRY_TRANSIENT_RETRY_MS = 60 * 1000;

/**
 * Minimum time between expiry-monitor gate refreshes. Prevents a scheduling
 * bug from tight-looping `/gate` after a successful refresh re-arms.
 */
export const GUEST_EXPIRY_MIN_REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Browsers store `setTimeout` delays as signed 32-bit ints; larger values
 * overflow and fire immediately (~24.8 days).
 */
export const GUEST_EXPIRY_MAX_TIMEOUT_MS = 2_147_483_647;

const TRANSIENT_GATE_CODES = new Set(['rate_limited', 'verify_locked_out']);

export type GuestExpirySnapshot = {
  expired: boolean;
  revoked: boolean;
  expiresAt?: string | null;
};

export type GuestAccessExpiryHandle = {
  dispose: () => void;
};

export type GuestAccessExpiryOptions = {
  expiresAt: string | null | undefined;
  warningMs?: number;
  /** Injectable clock for tests (epoch ms). */
  now?: () => number;
  /**
   * Injectable scheduler for tests. Returns a clear function.
   * Default uses `setTimeout` clamped to {@link GUEST_EXPIRY_MAX_TIMEOUT_MS}.
   */
  schedule?: (fn: () => void, delayMs: number) => () => void;
  /** Delay after a transient gate failure before retrying (default 60s). */
  transientRetryMs?: number;
  /** Minimum interval between gate refreshes (default 60s). */
  minRefreshIntervalMs?: number;
  refreshExpiry: () => Promise<GuestExpirySnapshot>;
  onWarning: (remainingMs: number) => void;
  onWarningClear?: () => void;
  /** Optional notice when gate refresh is rate-limited (no logout). */
  onTransientRefreshError?: (code: string) => void;
  onExpiredLogout: () => void | Promise<void>;
};

function parseExpiresAt(value: string | null | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function transientGateCode(cause: unknown): string | null {
  if (cause instanceof ApiError && TRANSIENT_GATE_CODES.has(cause.code)) {
    return cause.code;
  }
  return null;
}

/**
 * Warns in the last few minutes before guest access expiry, then re-checks the
 * gate. If an admin extended access, reschedules; otherwise logs the guest out.
 */
export function startGuestAccessExpiryMonitor(
  options: GuestAccessExpiryOptions,
): GuestAccessExpiryHandle {
  const warningMs = options.warningMs ?? GUEST_EXPIRY_WARNING_MS;
  const transientRetryMs = options.transientRetryMs ?? GUEST_EXPIRY_TRANSIENT_RETRY_MS;
  const minRefreshIntervalMs =
    options.minRefreshIntervalMs ?? GUEST_EXPIRY_MIN_REFRESH_INTERVAL_MS;
  const now = options.now ?? (() => Date.now());
  const schedule =
    options.schedule ??
    ((fn, delayMs) => {
      const capped = Math.min(Math.max(0, delayMs), GUEST_EXPIRY_MAX_TIMEOUT_MS);
      const id = window.setTimeout(fn, capped);
      return () => window.clearTimeout(id);
    });

  let disposed = false;
  let clearWarning: (() => void) | null = null;
  let clearExpiry: (() => void) | null = null;
  let warningShown = false;
  let checking = false;
  let lastRefreshAt: number | null = null;
  /** Latest expiresAt passed to arm — used when a capped wake re-enters arm. */
  let armedExpiresAt: string | null | undefined = options.expiresAt;

  const clearTimers = () => {
    clearWarning?.();
    clearWarning = null;
    clearExpiry?.();
    clearExpiry = null;
  };

  const scheduleRefreshRetry = (delayMs: number) => {
    clearExpiry?.();
    clearExpiry = schedule(() => {
      clearExpiry = null;
      void runExpiryCheck();
    }, delayMs);
  };

  const arm = (expiresAt: string | null | undefined) => {
    clearTimers();
    if (disposed) return;

    armedExpiresAt = expiresAt;
    const expiresMs = parseExpiresAt(expiresAt);
    if (expiresMs === null) {
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      return;
    }

    const remaining = expiresMs - now();
    if (remaining <= 0) {
      void runExpiryCheck();
      return;
    }

    // Far-future expiry: wake later and re-arm. Never warn or hit the gate yet.
    if (remaining > GUEST_EXPIRY_MAX_TIMEOUT_MS) {
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      clearExpiry = schedule(() => {
        clearExpiry = null;
        if (disposed) return;
        arm(armedExpiresAt);
      }, GUEST_EXPIRY_MAX_TIMEOUT_MS);
      return;
    }

    if (remaining <= warningMs) {
      if (!warningShown) {
        warningShown = true;
        options.onWarning(remaining);
      }
    } else {
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      const warningDelay = remaining - warningMs;
      clearWarning = schedule(() => {
        clearWarning = null;
        if (disposed) return;
        // Capped schedule woke early — re-arm instead of showing a bogus warning.
        if (expiresMs - now() > warningMs) {
          arm(armedExpiresAt);
          return;
        }
        warningShown = true;
        options.onWarning(Math.max(0, expiresMs - now()));
      }, warningDelay);
    }

    clearExpiry = schedule(() => {
      clearExpiry = null;
      if (disposed) return;
      // Capped schedule woke early — re-arm instead of treating as expiry.
      if (expiresMs - now() > 0) {
        arm(armedExpiresAt);
        return;
      }
      void runExpiryCheck();
    }, remaining);
  };

  const scheduleTransientRetry = (code: string) => {
    options.onTransientRefreshError?.(code);
    scheduleRefreshRetry(transientRetryMs);
  };

  const runExpiryCheck = async () => {
    if (disposed || checking) return;

    if (lastRefreshAt !== null) {
      const sinceLast = now() - lastRefreshAt;
      if (sinceLast < minRefreshIntervalMs) {
        scheduleRefreshRetry(minRefreshIntervalMs - sinceLast);
        return;
      }
    }

    checking = true;
    try {
      lastRefreshAt = now();
      const snapshot = await options.refreshExpiry();
      if (disposed) return;

      if (snapshot.revoked || snapshot.expired) {
        if (warningShown) {
          warningShown = false;
          options.onWarningClear?.();
        }
        await options.onExpiredLogout();
        return;
      }

      const nextExpiresMs = parseExpiresAt(snapshot.expiresAt);
      // Admin cleared expiry — no further countdown.
      if (nextExpiresMs === null) {
        if (warningShown) {
          warningShown = false;
          options.onWarningClear?.();
        }
        return;
      }

      // Admin extended (or clock skew): reschedule from the fresh timestamp.
      if (nextExpiresMs > now()) {
        arm(snapshot.expiresAt);
        return;
      }

      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      await options.onExpiredLogout();
    } catch (cause) {
      if (disposed) return;
      const transient = transientGateCode(cause);
      if (transient) {
        // Rate limits must not look like access expiry — retry without logout.
        scheduleTransientRetry(transient);
        return;
      }

      // Gate refresh failed — treat as expired to avoid lingering access.
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
      await options.onExpiredLogout();
    } finally {
      checking = false;
    }
  };

  arm(options.expiresAt);

  return {
    dispose: () => {
      if (disposed) return;
      disposed = true;
      clearTimers();
      if (warningShown) {
        warningShown = false;
        options.onWarningClear?.();
      }
    },
  };
}
